import { ArrowUpRight, SlidersHorizontal } from 'lucide-react';
import type { VendorDataset } from '@/lib/data/vendor-datasets/types';

interface VendorPickerCardProps {
  dataset: VendorDataset;
}

/**
 * Compact preview card rendered in blog posts in place of an inline VendorPicker
 * when the dataset is large. A full picker renders every vendor as a tall card
 * (blurb + highlights + tradeoffs + pricing), so a 10-vendor dataset is ~3,000px
 * tall and swamps the surrounding article (see MarkdownContent's threshold guard).
 *
 * Instead we show a small card that links to the canonical interactive tool at
 * /tools/comparison/{toolSlug}. Renders server-side — no `'use client'` needed.
 *
 * Visual style mirrors ToolPreviewCard for consistency across blog tool embeds.
 */
export default function VendorPickerCard({ dataset }: VendorPickerCardProps) {
  // Datasets without a toolSlug have no canonical page; fall back to the index.
  const href = dataset.toolSlug
    ? `/tools/comparison/${dataset.toolSlug}`
    : '/tools/comparison';

  return (
    <div className="not-prose my-8 rounded-lg border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300">
        <SlidersHorizontal className="h-3.5 w-3.5" />
        Interactive comparison
      </div>
      <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">
        {dataset.topic}
      </h3>
      <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">{dataset.intro}</p>
      <a
        href={href}
        className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-800 dark:bg-blue-600 dark:hover:bg-blue-700"
      >
        Compare all {dataset.vendors.length} options
        <ArrowUpRight className="h-4 w-4" />
      </a>
    </div>
  );
}
